"use client";

import type { BlogPostCard, EnrichedBlogPost } from "@/lib/supabase/types";
import {
	cn,
	getBlogPostUrl,
	getReadableNumber,
	getReadingTime,
} from "@/lib/utils";
import { ClockFading, Eye, MessageSquareMore } from "lucide-react";
import { AnimatePresence } from "motion/react";
import Image, { type ImageProps } from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import ShareButton from "../ui/share-button";
import BookmarkButton from "./bookmark-button";
import ReactionPicker from "./reaction-picker";

const MAX_TAGS = 2;

type PostCardProps = {
	post: BlogPostCard | EnrichedBlogPost;
	className?: string;
};

export function PostCard({ post, className }: PostCardProps) {
	const router = useRouter();
	const [hovered, setHovered] = useState(false);
	const link = getBlogPostUrl(post.slug);
	const commentsCount =
		"comments" in post
			? (post as EnrichedBlogPost).comments?.length || 0
			: (post as BlogPostCard).comments_count || 0;

	return (
		<div
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={cn(
				"relative z-5 row-span-1 flex flex-col justify-between items-start overflow-hidden rounded-3xl bg-black/80",
				"hover:shadow-xl hover:shadow-black/20 min-h-[240px] w-full",
				className,
			)}
		>
			<AnimatePresence>
				<div
					key={`content-${post.id}`}
					className="pointer-events-none absolute inset-x-0 top-0 z-6 h-full bg-gradient-to-b from-black/50 via-transparent to-transparent"
				/>

				<div
					key={`main-${post.id}`}
					className="flex flex-col justify-between h-full w-full z-[8]"
				>
					<div className="relative w-full z-7">
						<div className="w-full h-[80px] z-[-1] absolute bg-black/50 backdrop-blur-[100px] mask-to-t" />
						<div className="flex flex-row justify-between">
							<div className="flex gap-1 m-2 w-full z-8">
								{post.tags?.slice(0, MAX_TAGS).map((tag) => {
									const tagId = tag.id || tag.tag_id;
									const tagName = tag.name || tag.tag?.name || "unknown";

									if (!tagName) {
										console.warn("Invalid tag structure:", tag);
										return null;
									}

									return (
										<Link
											key={`${post.id}-${tagName || tagId}`}
											href={`/blog?tags=${tagName || tagId}`}
											className="text-center rounded-2xl w-fit h-fit bg-background/40 px-2 py-1 font-sans text-xs font-semibold text-primary hover:bg-background"
										>
											{String(tagName)}
										</Link>
									);
								})}
							</div>
							<div className="flex mx-2 gap-1 w-full items-center justify-end">
								<div className="flex gap-1 px-1 items-center text-white">
									<Eye className={"size-4"} />
									<span className="font-sans text-xs">
										<span className="sr-only">Total views: </span>
										{post.metadata?.views_count &&
											getReadableNumber(post.metadata?.views_count)}
									</span>
								</div>
								<BookmarkButton postId={post.id} dark={true} expanded={false} />
								<ShareButton
									type="post"
									title={post.title}
									slug={post.slug}
									post={post as BlogPostCard}
									url={link}
									className="text-white"
								/>
							</div>
						</div>
					</div>
					<div className="relative w-full h-full z-7">
						<div className="w-full h-full z-[-1] absolute bg-black/50 backdrop-blur-[100px] mask-to-b" />
						<div className="absolute bottom-0 w-full p-3 z-8">
							<Link href={link}>
								<p className="w-full py-4 text-left font-bold text-lg transition-all [text-wrap:balance] text-white">
									{post.title}
								</p>
							</Link>

							{/* <div className="flex flex-row gap-2 justify-between">
									<motion.p className="text-left font-light text-xs transition-all [text-wrap:balance] text-gray-300">
										{post.description && post.description?.length > 69
											? `${post.description?.substring(0, 69)}...`
											: post.description}
									</motion.p>
									<p className="font-sans text-[10px] text-nowrap h-full place-self-end font-light text-white">
										{post.created_at && timeAgo(new Date(post.created_at))}
									</p>
								</div> */}

							<hr className="hidden md:visible w-full my-2 opacity-15" />

							<div className="w-full flex flex-row justify-between items-center">
								<div className="flex gap-1 items-center">
									<ReactionPicker postId={post.id} />
									<span className="mx-1 text-white">•</span>
									<Link
										href={`${link}#comments`}
										className={cn(
											"font-sans text-[11px] font-semibold text-white",
											"hover:underline hover:underline-offset-4",
											"transition-all",
										)}
									>
										<div className="flex gap-1 items-center">
											<MessageSquareMore
												className={cn(
													"size-4 text-white transition-all",
													hovered ? "scale-110 -rotate-12" : "",
												)}
											/>
											<span className="sr-only">Comments: </span>
											{getReadableNumber(commentsCount)}
										</div>
									</Link>
								</div>
								<div className="flex gap-1 items-center">
									<div className="flex gap-1 items-center">
										<span className="font-sans text-[11px] font-light text-gray-300">
											<span className="sr-only">Reading time: </span>
											{getReadingTime(post.content)}
										</span>
										<ClockFading className="size-4 text-white" />
									</div>
									<button
										type="button"
										onClick={() => {
											router.push(link);
											router.refresh();
										}}
										className="inline-flex items-center rounded-xl px-2 py-1 text-xs font-bold text-white bg-background/40 hover:bg-background/60 hover:border-primary"
									>
										Read
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<BlurImage
					key={`image-${post.id}`}
					src={post.image || ""}
					alt={post.title}
					// fill
					className={cn(
						"absolute inset-0 z-1 object-cover aspect-[16/9]",
						hovered ? "scale-105" : "",
					)}
					width={240}
					height={135}
				/>
			</AnimatePresence>
		</div>
	);
}

export const BlurImage = ({
	height,
	width,
	src,
	className,
	alt,
	...rest
}: ImageProps) => {
	const [isLoading, setLoading] = useState(true);
	return (
		<Image
			className={cn(
				"h-full w-full transition duration-300 hover:scale-105",
				isLoading ? "blur-sm" : "blur-0",
				className,
			)}
			onLoad={() => setLoading(false)}
			src={src as string}
			width={width}
			height={height}
			loading="lazy"
			decoding="async"
			blurDataURL={typeof src === "string" ? src : undefined}
			alt={alt ? alt : "Background of a beautiful view"}
			{...rest}
		/>
	);
};
